import { Autocomplete, AutocompleteInputChangeReason, TextField } from '@mui/material';
import { useMemo } from 'react';
import { UpsertCatalogProductsDialog } from '../../../../../products/ProductsPage/products-page-components/CreateProductDialog/UpsertCatalogProductsDialog';
import { isNotNullOrUndefined, isObject, isString, Optional } from '@vy/utils';
import {
  PRODUCT_OPTION_TYPE,
  type ProductOption,
} from '../fixed-charges-edit-table-types/product-option-type';
import { renderOption } from './fixed-product-name-picker-render-option';
import { INPUT_REASONS_VALUES } from './fixed-charges-edit-table-types/inputs-reasons-types';
import { isAddProduct } from '../fixed-charges-edit-table-utils';
import { useToggle } from '../../../../../../hooks/use-toggle';
import { Size, SIZES } from '../../../../../../components/types/sizes';

type FixedProductNamePickerProps = {
  value: string;
  placeholder: string;
  catalogProductsNames: string[];
  onChange: (newValue: Optional<string>, option?: ProductOption) => void;
  size?: Extract<Size, typeof SIZES.SMALL | typeof SIZES.MEDIUM>;
  onProductCreated?: () => void;
  'data-vy'?: string;
};

export const FixedProductNamePicker: React.FC<FixedProductNamePickerProps> = ({
  value,
  onChange,
  placeholder,
  catalogProductsNames,
  size = SIZES.SMALL,
  onProductCreated,
  ...rest
}) => {
  const [showCreateDialog, toggleShowCreateDialog] = useToggle(false);

  const options = useMemo((): ProductOption[] => {
    const catalogOptions: ProductOption[] = getCatalogOptions(catalogProductsNames);

    const addProductOption: ProductOption = getAddProductOption();

    return [...catalogOptions, addProductOption];
  }, [catalogProductsNames]);

  const selectedOption = getSelectedOption(options, value);

  const handleSelectionChange = (newValue: Optional<ProductOption | string>) => {
    const isAddProductOption = isObject(newValue) && isAddProduct(newValue);

    if (isAddProductOption) {
      toggleShowCreateDialog();
      return;
    }

    const value = isString(newValue) ? newValue : newValue?.value;

    onChange(value ?? null);
  };

  const handleCloseCreateDialog = () => {
    toggleShowCreateDialog();
    onChange(null);
  };

  const handleProductCreated = (productName: string) => {
    if (isNotNullOrUndefined(onProductCreated)) {
      onProductCreated();
    }

    onChange(productName);
  };

  const handleInputChange = (
    _: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (INPUT_REASONS_VALUES.includes(reason)) {
      onChange(newInputValue);
    }
  };

  return (
    <>
      <div className="w-full">
        <Autocomplete
          fullWidth
          freeSolo
          size={size}
          options={options}
          getOptionLabel={(option) => (isString(option) ? option : option.label)}
          renderOption={renderOption}
          placeholder={placeholder}
          {...rest}
          renderInput={(params) => (
            <TextField
              placeholder="Service name"
              {...params}
              slotProps={{
                ...params.slotProps,

                htmlInput: {
                  ...params.slotProps.htmlInput,
                }
              }}
            />
          )}
          onChange={(_, newValue) => {
            handleSelectionChange(newValue);
          }}
          inputValue={selectedOption?.label ?? value ?? ''}
          onInputChange={handleInputChange}
          value={selectedOption}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </div>
      <UpsertCatalogProductsDialog
        openCreateProductDialog={showCreateDialog}
        onClose={handleCloseCreateDialog}
        onProductCreated={handleProductCreated}
      />
    </>
  );
};

function getCatalogOptions(catalogProductsNames: string[]) {
  return catalogProductsNames.map((name) => ({
    label: name,
    value: name,
    type: PRODUCT_OPTION_TYPE.CATALOG,
  }));
}

function getSelectedOption(options: ProductOption[], value: string) {
  return options.find((option) => option.value === value && !isAddProduct(option));
}

function getAddProductOption(): ProductOption {
  return {
    label: 'Add product',
    value: '__add_product__',
    type: PRODUCT_OPTION_TYPE.ADD_PRODUCT,
  };
}
